import { useEffect, useState } from "react";
import { Activity, AlertTriangle, BellRing, CalendarClock, CheckCircle2, ChevronRight, Clock, RefreshCw, Route, XCircle } from "lucide-react";
import { useSystemHealthEscalations, useSystemHealthJobList, useSystemHealthJobs } from "../../lib/api";
import {
  Chip, Crumbs, FailedToLoad, Heading, IconChip, Loading, MigrationPending, Notice, PAGE, Pager, Segmented,
  dangerButton, endpoint, focusRing, panel, pillClass, pillStyle, primaryButton,
} from "./healthUi";
import {
  JOB_STATE_LABELS, LOOP_STATE_LABELS, formatAgo, formatIn, formatWhen, jobStateLabel, jobStateTone, ownerLabel,
} from "./healthFormat";

// Background jobs tab: whether the background checks are running, two parts: the jobs
// behind each journey stage and the escalation ladders, then what is scheduled next. Opening a row
// shows just those jobs (or steps) and why they failed.
//   overview -> a stage's / every job (list) | a ladder's failed steps (ladder)

const MIGRATION = "20261008100000_add_job_tracking.sql";
const OVERVIEW = { level: "overview" };

const STATE_ICONS = { good: CheckCircle2, bad: XCircle, warn: AlertTriangle, idle: Clock };

// "upcoming" is every job still to run (scheduled or trying again), soonest first.
const UPCOMING = "upcoming";
const STATE_FILTERS = [
  { key: "", label: "All" },
  { key: UPCOMING, label: "Scheduled" },
  { key: "running", label: JOB_STATE_LABELS.running },
  { key: "done", label: JOB_STATE_LABELS.done },
  { key: "skipped", label: JOB_STATE_LABELS.skipped },
  { key: "failed", label: JOB_STATE_LABELS.failed },
  { key: "late", label: JOB_STATE_LABELS.late },
  { key: "stuck", label: JOB_STATE_LABELS.stuck },
];
const STILL_TO_RUN = new Set(["waiting", "retrying"]);
const FINISHED = new Set(["done", "skipped", "failed"]);

// The time now, moved on every minute, so "runs in 25 min" counts down while the page is open.
function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

// When a job runs or ran: a countdown while it waits, the finish time once it is over.
function whenLine(job, now) {
  if (STILL_TO_RUN.has(job.state)) return `runs ${formatIn(job.run_at, now)} (${formatWhen(job.run_at)})`;
  if (FINISHED.has(job.state)) return `finished ${formatWhen(job.finished_at || job.run_at)}`;
  return `due ${formatWhen(job.run_at)}`;
}

const sentence = (text) => `${String(text || "").trim().replace(/[.\s]+$/, "")}.`;

// Only why it failed and who fixes it.
function Reason({ reason }) {
  if (!reason) return null;
  return (
    <p className="mt-1 text-[13px] text-[#4B5563]">
      {sentence(reason.meaning)}{" "}
      <span className="text-[#6B7280]">{reason.will_retry ? "It will be tried again by itself." : reason.owner === "none" ? "No action needed." : `${ownerLabel(reason.owner)} to fix.`}</span>
    </p>
  );
}

const bookingLine = (booking) =>
  [booking?.group_name, booking?.workshop].filter(Boolean).join(" · ") || "No booking linked";

// ---- overview ---------------------------------------------------------------

// The three background checks that run every waiting job, as the engine room of the
// page: a dark strip, one light per check. A stopped one is the most urgent fact
// here, so it gets its own words.
function Checks({ report }) {
  const late = report.waiting_past_time;
  return (
    <section aria-label="Background checks" className="rounded-2xl bg-[#0F172A] px-5 py-4 text-white sm:px-6">
      <div className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.08em] text-[#94A3B8]">
        <Activity size={14} aria-hidden="true" /> Background checks
      </div>
      <ul className="mt-3 grid gap-x-6 gap-y-4 sm:grid-cols-3">
        {report.loops.map((loop) => {
          const ok = loop.state === "ok";
          return (
            <li key={loop.key} className="flex min-w-0 items-start gap-3">
              <span className="relative mt-1.5 flex h-2.5 w-2.5 flex-none">
                {ok && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#4ADE80] opacity-60 motion-reduce:hidden" />}
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: ok ? "#4ADE80" : "#F87171" }} />
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold">{`${loop.label} · ${loop.service}`}</span>
                <span className="block text-[12px]" style={{ color: ok ? "#94A3B8" : "#FCA5A5" }}>
                  {loop.state === "never"
                    ? "Has not reported. Is it switched on?"
                    : `${LOOP_STATE_LABELS[loop.state]} · last ran ${formatAgo(loop.seconds_ago)}`}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      {late > 0 && (
        <p className="mt-4 border-t border-[#1E293B] pt-3 text-[13px] font-medium text-[#FCA5A5]">
          {late} {late === 1 ? "job is" : "jobs are"} past {late === 1 ? "its" : "their"} time and nothing has run {late === 1 ? "it" : "them"}.
        </p>
      )}
    </section>
  );
}

// One line of a panel: a button when there is something to open, plain otherwise.
function Row({ onClick, children }) {
  const row = "-mx-2 flex min-h-14 w-[calc(100%+16px)] items-center justify-between gap-4 rounded-xl px-2 py-3 text-left";
  return onClick ? (
    <button type="button" onClick={onClick} className={`${row} cursor-pointer hover:bg-[#F9FAFB] ${focusRing}`}>
      {children}
      <ChevronRight size={16} aria-hidden="true" className="flex-none text-[#9CA3AF]" />
    </button>
  ) : (
    <div className={row}>{children}</div>
  );
}

// What runs next, soonest first, each with a countdown.
function Scheduled({ report, open }) {
  const now = useNow();
  const jobs = report.upcoming || [];
  const total = report.upcoming_total || 0;
  return (
    <section aria-label="Scheduled" className={`${panel} p-5 sm:p-6`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[17px] font-semibold text-[#111827]">
          <CalendarClock size={18} aria-hidden="true" className="text-[#2667B5]" /> Scheduled
        </h2>
        {total > jobs.length && (
          <button type="button" onClick={() => open({ level: "list", state: UPCOMING, label: "Scheduled" })} className={primaryButton}>
            {`See all ${total}`}
          </button>
        )}
      </div>
      {jobs.length === 0 ? (
        <p className="mt-4 text-[13px] text-[#6B7280]">Nothing is scheduled.</p>
      ) : (
        <ul className="mt-2 divide-y divide-[#F3F4F6]">
          {jobs.map((job) => (
            <li key={`${job.service}-${job.id}`} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-3">
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-[#111827]">{job.label}</span>
                <span className="block text-[12px] text-[#6B7280]">{`${bookingLine(job.booking)} · ${formatWhen(job.run_at)}`}</span>
              </span>
              <Chip tone={job.state === "retrying" ? "warn" : "idle"}>
                {`${job.state === "retrying" ? "Tries again" : "Runs"} ${formatIn(job.run_at, now)}`}
              </Chip>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Part one: the jobs behind each stage, in the order a booking goes through them.
function Journey({ report, open }) {
  const stages = report.by_stage;
  return (
    <section className={`${panel} p-5 sm:p-6`}>
      <h2 className="flex items-center gap-2 text-[17px] font-semibold text-[#111827]">
        <Route size={18} aria-hidden="true" className="text-[#2667B5]" /> Jobs by stage
      </h2>
      {stages.length === 0 ? (
        <p className="mt-4 text-[13px] text-[#6B7280]">No background jobs in this period.</p>
      ) : (
        <ul className="mt-2 divide-y divide-[#F3F4F6]">
          {stages.map((s) => {
            const total = s.done + s.open + s.attention;
            return (
              <li key={s.stage || "other"}>
                <Row onClick={() => open({ level: "list", stage: s.stage, label: s.label })}>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-semibold text-[#111827]">{s.label}</span>
                    <span className="block text-[12px] text-[#6B7280]">
                      {[s.done && `${s.done} finished`, s.open && `${s.open} waiting`].filter(Boolean).join(" · ") || "Nothing finished yet"}
                    </span>
                    {total > 0 && (
                      <span aria-hidden="true" className="mt-2 flex h-1.5 w-full max-w-[280px] overflow-hidden rounded-full bg-[#F3F4F6]">
                        <span style={{ width: `${(100 * s.done) / total}%`, background: "#16A34A" }} />
                        <span style={{ width: `${(100 * s.open) / total}%`, background: "#CBD5E1" }} />
                        <span style={{ width: `${(100 * s.attention) / total}%`, background: "#DC2626" }} />
                      </span>
                    )}
                  </span>
                  {s.attention > 0 ? (
                    <Chip tone="bad">{`${s.attention} ${s.attention === 1 ? "needs" : "need"} attention`}</Chip>
                  ) : (
                    <Chip tone="good">All fine</Chip>
                  )}
                </Row>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// Part two: the escalation ladders. A ladder with failed steps opens to show them.
function Escalations({ query, open }) {
  const { data, isLoading, error, refetch } = query;
  if (isLoading) return <Loading />;
  if (error) return <FailedToLoad error={error} retry={refetch} />;
  if (data?.migration_pending) return <MigrationPending file={MIGRATION} />;
  const { ladders } = data.report;
  return (
    <section title={endpoint("escalations")} className={`${panel} p-5 sm:p-6`}>
      <h2 className="flex items-center gap-2 text-[17px] font-semibold text-[#111827]">
        <BellRing size={18} aria-hidden="true" className="text-[#D97706]" /> Escalations
      </h2>
      <ul className="mt-2 divide-y divide-[#F3F4F6]">
        {ladders.map((l) => {
          const problem = l.failed + l.retrying > 0;
          return (
            <li key={l.key}>
              <Row onClick={problem ? () => open({ level: "ladder", ladder: l.key, label: l.label }) : undefined}>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold text-[#111827]">{l.label}</span>
                  <span className="block text-[12px] text-[#6B7280]">{`${l.open} open · ${l.sent} sent`}</span>
                </span>
                <span className="flex flex-wrap justify-end gap-1.5">
                  {l.retrying > 0 && <Chip tone="warn">{`${l.retrying} will try again`}</Chip>}
                  {l.failed > 0 && <Chip tone="bad">{`${l.failed} given up`}</Chip>}
                  {!problem && <Chip tone="good">All fine</Chip>}
                </span>
              </Row>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Overview({ jobsQuery, escalationsQuery, open }) {
  const { data, isLoading, error, refetch } = jobsQuery;
  if (isLoading) return <Loading />;
  if (error) return <FailedToLoad error={error} retry={refetch} />;
  if (data?.migration_pending) return <MigrationPending file={MIGRATION} />;
  const { report } = data;
  const { attention, ran, total } = report.headline;
  const given = (escalationsQuery.data?.report?.ladders || []).reduce((sum, l) => sum + l.failed, 0);
  const things = attention + given;
  // A check that is off is the most urgent fact here: every job that waits depends on it.
  // Only the Super Admin is told about the checks: they run for every brand at once.
  const down = (report.loops || []).filter((l) => l.state !== "ok");
  const headline = down.length
    ? "Background checks have stopped"
    : things
      ? `${things} ${things === 1 ? "thing needs" : "things need"} attention`
      : total
        ? "Everything is running"
        : "No background jobs in this period";
  const detail = down.length
    ? `${down.map((l) => `${l.label} (${l.service})`).join(", ")} ${down.length === 1 ? "has" : "have"} not reported lately. Jobs that wait may not run.`
    : total
      ? [
          `${ran} ${ran === 1 ? "job" : "jobs"} ran in this period`,
          attention > 0 && `${attention} failed or late`,
          given > 0 && `${given} escalation ${given === 1 ? "step" : "steps"} given up`,
        ]
          .filter(Boolean)
          .join(" · ")
      : "";
  const wrong = down.length > 0 || things > 0;
  return (
    <div title={endpoint("jobs")} className="mt-6 flex flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div role="status" className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          {wrong ? (
            <AlertTriangle size={18} aria-hidden="true" className="flex-none text-[#DC2626]" />
          ) : (
            <CheckCircle2 size={18} aria-hidden="true" className="flex-none text-[#16A34A]" />
          )}
          <Heading className="text-[15px] font-semibold text-[#111827]">{headline}</Heading>
          {detail && <span className="text-[13px] text-[#6B7280]">{detail}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {attention > 0 && (
            <button type="button" onClick={() => open({ level: "list", attention: true, label: "Needs attention" })} className={dangerButton}>
              Show them
            </button>
          )}
          <button type="button" onClick={() => open({ level: "list", label: "All jobs" })} className={primaryButton}>
            See all jobs
          </button>
        </div>
      </header>

      {report.loops && <Checks report={report} />}
      <div className="grid items-start gap-5 lg:grid-cols-2">
        <Journey report={report} open={open} />
        <Escalations query={escalationsQuery} open={open} />
      </div>
      <Scheduled report={report} open={open} />
    </div>
  );
}

// ---- one ladder's failed steps ---------------------------------------------------

function LadderSteps({ query, view, range, open }) {
  const steps = (query.data?.report?.failed_steps || []).filter((s) => s.ladder === view.ladder);
  return (
    <div title={endpoint("escalations")} className="mt-2">
      <Crumbs trail={[{ label: "Background jobs", go: () => open(OVERVIEW) }, { label: view.label }]} period={range.label} />
      <Heading className="mt-3 text-[18px] font-semibold text-[#111827]">{view.label}</Heading>
      {steps.length === 0 ? (
        <Notice>No failed steps on this ladder in this period.</Notice>
      ) : (
        <ul className={`${panel} mt-4 divide-y divide-[#F3F4F6] px-5 sm:px-6`}>
          {steps.map((s) => {
            const retrying = s.state === "retrying";
            // Nobody was left to remind: the step stopped, but there is nothing to fix.
            const harmless = s.reason?.owner === "none";
            const tone = harmless ? "idle" : retrying ? "warn" : "bad";
            return (
              <li key={`${s.sla_id}-${s.step}-${s.at}`} className="flex gap-4 py-4">
                <IconChip icon={harmless ? CheckCircle2 : retrying ? RefreshCw : XCircle} tone={tone} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                    <p className="text-[14px] font-semibold text-[#111827]">{bookingLine(s.booking)}</p>
                    <Chip tone={tone}>{harmless ? "Nothing to fix" : retrying ? "Will try again" : "Given up"}</Chip>
                  </div>
                  <p className="text-[12px] text-[#6B7280]">Step {s.step}</p>
                  <Reason reason={s.reason} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---- every job -----------------------------------------------------------------

function JobList({ scope, range, view, open }) {
  const now = useNow();
  const [state, setState] = useState(view.state || "");
  const [stage, setStage] = useState(view.stage || "");
  const [attention, setAttention] = useState(Boolean(view.attention));
  const [page, setPage] = useState(0);
  const { data, isLoading, error, refetch } = useSystemHealthJobList({
    ...scope, from: range.from(), state, stage, attention, limit: PAGE, offset: page * PAGE,
  });
  const pickState = (key) => {
    setState(key);
    setAttention(false);
    setPage(0);
  };
  return (
    <div title={endpoint("job-list")} className="mt-2">
      <Crumbs
        trail={[{ label: "Background jobs", go: () => open(OVERVIEW) }, { label: view.label || "All jobs" }]}
        period={range.label}
      />
      <Heading className="mt-3 text-[18px] font-semibold text-[#111827]">{view.label || "All jobs"}</Heading>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Segmented label="Filter by state" options={STATE_FILTERS} isOn={(key) => !attention && state === key} pick={pickState} />
        {attention && (
          <button type="button" onClick={() => { setAttention(false); setPage(0); }} aria-label="Remove the filter: needs attention" className={pillClass} style={pillStyle(true)}>
            Needs attention ×
          </button>
        )}
        {stage && (
          <button type="button" onClick={() => { setStage(""); setPage(0); }} aria-label="Remove the filter: journey stage" className={pillClass} style={pillStyle(true)}>
            {view.label} ×
          </button>
        )}
      </div>
      <div aria-live="polite">
      {isLoading ? (
        <Loading />
      ) : error ? (
        <FailedToLoad error={error} retry={refetch} />
      ) : data?.migration_pending ? (
        <MigrationPending file={MIGRATION} />
      ) : data.rows.length === 0 ? (
        <Notice>No jobs match this filter in this period.</Notice>
      ) : (
        <>
          <ul aria-label="Jobs" className={`${panel} mt-4 divide-y divide-[#F3F4F6] px-5 sm:px-6`}>
            {data.rows.map((job) => {
              const tone = jobStateTone(job.state);
              return (
                <li key={`${job.service}-${job.id}`} className="flex gap-4 py-4">
                  <IconChip icon={STATE_ICONS[tone] || Clock} tone={tone} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                      <p className="text-[14px] font-semibold text-[#111827]">{job.label}</p>
                      <Chip tone={tone}>{jobStateLabel(job.state)}</Chip>
                    </div>
                    <p className="text-[12px] text-[#6B7280]">
                      {[
                        bookingLine(job.booking), job.service === "webhook" ? "Webhook" : "API", whenLine(job, now),
                        job.attempts > 0 && `${job.attempts} ${job.attempts === 1 ? "try" : "tries"}`,
                      ].filter(Boolean).join(" · ")}
                    </p>
                    <Reason reason={job.reason} />
                  </div>
                </li>
              );
            })}
          </ul>
          <Pager page={page} shown={data.rows.length} total={data.total} onPage={setPage} />
        </>
      )}
      </div>
    </div>
  );
}

export default function HealthJobs({ scope, range }) {
  const [view, setView] = useState(OVERVIEW);
  const jobsQuery = useSystemHealthJobs({ ...scope, from: range.from() });
  const escalationsQuery = useSystemHealthEscalations({ ...scope, from: range.from() });
  if (view.level === "ladder") {
    return <LadderSteps query={escalationsQuery} view={view} range={range} open={setView} />;
  }
  if (view.level === "list") {
    // A new key for each opening, so the filters start from what was clicked.
    return <JobList key={JSON.stringify(view)} scope={scope} range={range} view={view} open={setView} />;
  }
  return <Overview jobsQuery={jobsQuery} escalationsQuery={escalationsQuery} open={setView} />;
}
